import { createContext, useContext, type ReactNode } from 'react'
import type { PreviewDemoCatalog } from '@/data/demoStorefrontData'

interface PreviewModeContextValue {
  isPreview: boolean
  catalogId: PreviewDemoCatalog
  /** Set in template preview so catalog queries never bleed across `/preview-template/:id` routes. */
  previewTemplateId: string | null
}

const PreviewModeContext = createContext<PreviewModeContextValue>({
  isPreview: false,
  catalogId: 'beauty',
  previewTemplateId: null,
})

export function PreviewModeProvider({
  children,
  catalogId = 'beauty',
  previewTemplateId = null,
}: {
  children: ReactNode
  /** Demo vertical — must match `getPreviewCategories` / `getPreviewProducts` in demoStorefrontData. */
  catalogId?: PreviewDemoCatalog
  previewTemplateId?: string | null
}) {
  return (
    <PreviewModeContext.Provider value={{ isPreview: true, catalogId, previewTemplateId }}>
      {children}
    </PreviewModeContext.Provider>
  )
}

/** Returns true when the current render is inside a template preview (not production). */
export function usePreviewMode(): boolean {
  return useContext(PreviewModeContext).isPreview
}

/** Active demo catalog in template preview; meaningless when `usePreviewMode()` is false. */
export function usePreviewCatalogId(): PreviewDemoCatalog {
  return useContext(PreviewModeContext).catalogId
}

export function usePreviewTemplateId(): string | null {
  return useContext(PreviewModeContext).previewTemplateId
}
